import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, DollarSign, Target, Calendar, MapPin, Music, TrendingUp, ExternalLink, ChevronRight, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { artistsAPI, socialAPI, guidanceAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import WatchButton from '../components/guidance/WatchButton';
import { useSmartNotify } from '../components/guidance/SmartNotificationProvider';
import { toast } from 'sonner';

export default function ArtistProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { triggerSmartNotify } = useSmartNotify();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await artistsAPI.getProfile(id);
        setArtist(res.data);
        if (user) {
          guidanceAPI.trackActivity('view', 'artist', id, res.data?.name || '').catch(() => {});
          try { const f = await socialAPI.isFollowing(id); setIsFollowing(f.data.is_following); } catch {}
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, user]);

  const toggleFollow = async () => {
    try {
      if (isFollowing) { await socialAPI.unfollow(id); setIsFollowing(false); toast.success('Unfollowed'); }
      else { await socialAPI.follow(id); setIsFollowing(true); toast.success('Following!'); triggerSmartNotify('followed', 'artist', id); }
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-2 border-[#00aa55] border-t-transparent rounded-full animate-spin" /></div>;
  if (!artist) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><p className="text-zinc-500">Artist not found</p></div>;

  const s = artist.stats || {};

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="artist-profile-page">
      {/* Cover */}
      <div className="relative h-48 md:h-64 bg-zinc-900 overflow-hidden">
        {artist.cover_image && <img src={artist.cover_image} alt="" className="w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 to-transparent" />
        <Link to="/projects" className="absolute top-4 left-4 text-white/70 hover:text-white"><ArrowLeft size={24} /></Link>
      </div>

      {/* Profile Header */}
      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <div className="w-28 h-28 rounded-2xl border-4 border-white bg-zinc-200 overflow-hidden shadow-xl flex-shrink-0">
            {artist.avatar ? <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-zinc-300 flex items-center justify-center text-3xl font-bold text-zinc-500">{artist.name?.[0]}</div>}
          </div>
          <div className="flex-1 pb-1">
            <h1 className="font-display font-bold text-2xl md:text-3xl text-zinc-900" data-testid="artist-name">{artist.name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500">
              {artist.genre && <span className="px-2 py-0.5 bg-zinc-200 rounded-full text-xs font-medium">{artist.genre}</span>}
              {artist.location && <span className="flex items-center gap-1"><MapPin size={12} /> {artist.location}</span>}
              <span className="flex items-center gap-1"><Users size={12} /> {s.followers} followers</span>
            </div>
          </div>
          {user && user.id !== artist.id && (
            <div className="flex items-center gap-2">
              <WatchButton entityType="artist" entityId={id} size="md" />
              <Button onClick={toggleFollow} className={isFollowing ? 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300' : 'bg-[#00aa55] text-white hover:bg-[#008844]'} data-testid="follow-btn">
                <Heart size={14} className={`mr-1 ${isFollowing ? 'fill-current' : ''}`} />
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            </div>
          )}
        </div>

        {artist.bio && <p className="text-zinc-600 text-sm mb-6 max-w-2xl leading-relaxed">{artist.bio}</p>}

        {/* Social Links */}
        {artist.social_links && Object.keys(artist.social_links).length > 0 && (
          <div className="flex gap-2 mb-6">
            {Object.entries(artist.social_links).map(([platform, url]) => url && (
              <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-full text-xs font-medium text-zinc-600 hover:bg-zinc-200 flex items-center gap-1">
                {platform} <ExternalLink size={10} />
              </a>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8">
          {[
            { label: 'Raised', value: formatCurrency(s.total_raised), color: 'text-[#00aa55]' },
            { label: 'Backers', value: s.total_backers, color: 'text-zinc-900' },
            { label: 'Projects', value: s.total_projects, color: 'text-zinc-900' },
            { label: 'Events', value: s.total_events, color: 'text-zinc-900' },
            { label: 'Tickets Sold', value: s.total_tickets_sold, color: 'text-zinc-900' },
            { label: 'Earned', value: formatCurrency(s.total_earned), color: 'text-[#00aa55]' },
          ].map(st => (
            <div key={st.label} className="p-3 bg-white rounded-xl border border-zinc-200 text-center">
              <p className={`font-display font-bold text-lg ${st.color}`}>{st.value}</p>
              <p className="text-[10px] text-zinc-400">{st.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="projects">
          <TabsList className="bg-white border border-zinc-200 rounded-xl p-1 mb-6">
            <TabsTrigger value="projects" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-projects">Projects ({artist.projects?.length || 0})</TabsTrigger>
            <TabsTrigger value="events" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-events">Events ({artist.events?.length || 0})</TabsTrigger>
            <TabsTrigger value="backers" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-backers">Backers ({artist.investments_received?.length || 0})</TabsTrigger>
            <TabsTrigger value="studio" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-studio">Studio ({artist.studio?.length || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artist.projects?.map(p => {
                const pct = p.funding_goal ? (p.current_funding / p.funding_goal * 100) : 0;
                return (
                  <Link key={p.id} to={`/projects/${p.id}`} className="block p-4 bg-white rounded-2xl border border-zinc-200 hover:border-[#00aa55]/40 hover:shadow-md transition-all" data-testid={`artist-project-${p.id}`}>
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />}
                    <h3 className="font-bold text-zinc-900 mb-1">{p.title}</h3>
                    <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{p.description}</p>
                    <div className="w-full bg-zinc-100 h-2 rounded-full mb-2">
                      <div className="h-2 rounded-full bg-[#00aa55]" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-zinc-500">
                      <span>{formatCurrency(p.current_funding)} raised</span>
                      <span>{pct.toFixed(0)}% of {formatCurrency(p.funding_goal)}</span>
                    </div>
                  </Link>
                );
              })}
              {!artist.projects?.length && <p className="text-zinc-400 text-sm col-span-2 text-center py-8">No projects yet</p>}
            </div>
          </TabsContent>

          <TabsContent value="events">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artist.events?.map(ev => (
                <Link key={ev.id} to={`/events/${ev.id}`} className="block p-4 bg-white rounded-2xl border border-zinc-200 hover:border-amber-400/40 hover:shadow-md transition-all" data-testid={`artist-event-${ev.id}`}>
                  <h3 className="font-bold text-zinc-900 mb-2">{ev.title}</h3>
                  <div className="space-y-1.5 text-xs text-zinc-500">
                    <div className="flex items-center gap-2"><Calendar size={12} className="text-amber-500" /> {ev.event_date} at {ev.event_time}</div>
                    <div className="flex items-center gap-2"><MapPin size={12} className="text-amber-500" /> {ev.venue || ev.location}</div>
                  </div>
                </Link>
              ))}
              {!artist.events?.length && <p className="text-zinc-400 text-sm col-span-2 text-center py-8">No events yet</p>}
            </div>
          </TabsContent>

          <TabsContent value="backers">
            <div className="space-y-2">
              {artist.investments_received?.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-zinc-200">
                  <div>
                    <span className="font-semibold text-zinc-800 text-sm">{inv.investor_name}</span>
                    {inv.tier_name && <span className="ml-2 text-xs text-[#00aa55] font-medium">{inv.tier_name}</span>}
                    <p className="text-xs text-zinc-400">{new Date(inv.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="font-bold text-[#00aa55]">{formatCurrency(inv.amount)}</span>
                </div>
              ))}
              {!artist.investments_received?.length && <p className="text-zinc-400 text-sm text-center py-8">No backers yet</p>}
            </div>
          </TabsContent>

          <TabsContent value="studio">
            <div className="space-y-3">
              {artist.studio?.map(item => (
                <div key={item.id} className="p-4 bg-white rounded-2xl border border-zinc-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.type === 'lyrics' ? 'bg-violet-100 text-violet-600' : item.type === 'album_art' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>{item.type}</span>
                    <span className="text-xs text-zinc-400">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                  {item.type === 'album_art' && item.image_url && <img src={item.image_url} alt="" className="w-32 h-32 rounded-xl object-cover" />}
                  {item.content && <p className="text-sm text-zinc-600 whitespace-pre-wrap line-clamp-6">{item.content}</p>}
                </div>
              ))}
              {!artist.studio?.length && <p className="text-zinc-400 text-sm text-center py-8">No studio content yet</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
